import axios from 'axios';
import { registerPlugin, Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Single location service for CivilX.
//
// Location is collected ONLY during an explicit load-tracking session that the
// driver starts from the Live Tracking screen and ends by tapping Stop (or when
// the load is delivered). Nothing here runs on login, app open or page views.
//
//  • Native (Capacitor iOS/Android): @capacitor-community/background-geolocation.
//    While a watcher exists the plugin sets allowsBackgroundLocationUpdates = true,
//    showsBackgroundLocationIndicator = true, pausesLocationUpdatesAutomatically = false
//    and calls startUpdatingLocation(); removeWatcher() calls stopUpdatingLocation()
//    and discards the manager (background updates off). Requires UIBackgroundModes
//    = [location] in Info.plist. We request "When In Use" in context and never
//    escalate to "Always" (requestPermissions: false).
//  • Web / PWA: navigator.geolocation.watchPosition (foreground only by design).
// ---------------------------------------------------------------------------
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
const CapApp = registerPlugin('App');
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'civilx_tracking_session';
const SEND_INTERVAL_MS = 60_000;
const TERMINAL_LOAD_STATUSES = new Set(['delivered', 'cancelled']);
// Persistent Android foreground-service notification (required while the service runs).
export const TRACKING_NOTIFICATION = {
  title: 'CivilX TMS — Location sharing active',
  message: 'Your trip progress is being shared with your dispatcher. Tap Stop in the app to end it.',
};

export const isNativeApp = () => Capacitor.isNativePlatform();

const session = { active: false, startedAt: null, lastFixAt: null, lastSentAt: null, status: 'Idle', permission: 'unknown', server: null, stopReason: null };
let webWatchId = null;
let nativeWatcherId = null;
let sendTimer = null;
let latestFix = null;
let lastSentFix = null;
let pendingFix = null; // kept when offline; flushed on the next tick
const listeners = new Set();

const notify = () => listeners.forEach((fn) => { try { fn({ ...session }); } catch { /* listener error */ } });
const setStatus = (status) => { session.status = status; notify(); };

export const getSession = () => ({ ...session });
export const isTrackingActive = () => session.active;
export const subscribe = (fn) => { listeners.add(fn); fn({ ...session }); return () => listeners.delete(fn); };

const toFix = (p) => ({
  lat: p.latitude ?? p.coords?.latitude,
  lng: p.longitude ?? p.coords?.longitude,
  accuracy: p.accuracy ?? p.coords?.accuracy ?? null,
  speed: p.speed ?? p.coords?.speed ?? null,
  heading: p.bearing ?? p.coords?.heading ?? null,
  recorded_at: new Date(p.time ?? p.timestamp ?? Date.now()).toISOString(),
});

// In-context "When In Use" prompt (WKWebView routes this to the app-level CLLocationManager prompt).
export const ensureLocationPermission = () => new Promise((resolve) => {
  if (!navigator.geolocation) { session.permission = 'unavailable'; return resolve({ granted: false, reason: 'unsupported' }); }
  navigator.geolocation.getCurrentPosition(
    () => { session.permission = 'granted'; resolve({ granted: true }); },
    (err) => {
      session.permission = err?.code === 1 ? 'denied' : 'unavailable';
      resolve({ granted: false, reason: err?.code === 1 ? 'denied' : err?.code === 2 ? 'unavailable' : 'timeout' });
    },
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
  );
});

const onFix = (fix) => {
  if (!session.active || fix.lat == null || fix.lng == null) return;
  latestFix = fix;
  session.lastFixAt = fix.recorded_at;
  if (!session.lastSentAt) sendNow(); // first fix goes out immediately
  else notify();
};

// Post the newest fix to the backend (every SEND_INTERVAL_MS while active). Retries the
// held-back fix after a network failure; the server dedupes identical coordinates.
export async function sendNow() {
  if (!session.active) return null;
  const fix = latestFix || pendingFix;
  if (!fix) return null;
  if (lastSentFix && lastSentFix.recorded_at === fix.recorded_at && lastSentFix.lat === fix.lat && lastSentFix.lng === fix.lng && !pendingFix) {
    return session.server; // nothing new since last send
  }
  try {
    const r = await axios.post(`${API}/driver/location`, fix, { withCredentials: true });
    lastSentFix = fix; pendingFix = null;
    session.lastSentAt = new Date().toISOString();
    session.server = r.data || {};
    // Trip complete → end the session (and the Android foreground service) no matter which screen is open.
    if (TERMINAL_LOAD_STATUSES.has(session.server.load_status)) {
      const ended = session.server;
      stopTracking(session.server.load_status);
      setStatus('Load delivered — location sharing stopped');
      return ended;
    }
    setStatus(session.server.tracking ? 'Live — sharing with dispatch' : 'On, but no active load assigned');
    return session.server;
  } catch {
    pendingFix = fix;
    setStatus('Network error — will retry');
    return null;
  }
}

const persist = () => {
  try {
    if (session.active) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ startedAt: session.startedAt }));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* storage blocked */ }
};

export async function startTracking() {
  if (session.active) return { ok: true };
  setStatus('Checking location permission…');
  const perm = await ensureLocationPermission();
  if (!perm.granted) {
    setStatus(perm.reason === 'denied'
      ? 'Location permission is required — allow it for CivilX in Settings, then try again'
      : perm.reason === 'unsupported' ? 'Location is not available on this device'
        : 'Location services are unavailable — check that Location Services are turned on');
    return { ok: false, reason: perm.reason };
  }

  session.active = true;
  session.startedAt = new Date().toISOString();
  session.lastFixAt = null; session.lastSentAt = null; session.server = null; session.stopReason = null;
  latestFix = null; lastSentFix = null; pendingFix = null;
  setStatus('Getting your location…');

  if (isNativeApp()) {
    try {
      nativeWatcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: TRACKING_NOTIFICATION.title,
          backgroundMessage: TRACKING_NOTIFICATION.message,
          requestPermissions: false, // we already asked for "While using the app" above; never escalate to "Always"/background
          stale: false,
          distanceFilter: 50, // metres — automotive movement; avoids draining the battery while parked
        },
        (location, error) => {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              setStatus('Location permission was revoked — tracking stopped');
              stopTracking();
            } else setStatus('GPS signal lost — waiting for a fix');
            return;
          }
          if (location) onFix(toFix(location));
        },
      );
    } catch {
      session.active = false;
      setStatus('Could not start location tracking');
      return { ok: false, reason: 'plugin' };
    }
  } else {
    webWatchId = navigator.geolocation.watchPosition(
      (pos) => onFix(toFix(pos)),
      (err) => setStatus(err?.code === 1 ? 'Location permission denied — tracking stopped' : 'GPS signal lost — waiting for a fix'),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
    );
  }
  sendTimer = setInterval(sendNow, SEND_INTERVAL_MS);
  persist();
  notify();
  return { ok: true };
}

export function stopTracking(reason = 'manual') {
  if (nativeWatcherId) {
    // removeWatcher → Android: removeLocationUpdates + stopForeground (notification dismissed);
    // iOS: stopUpdatingLocation and the CLLocationManager is discarded.
    BackgroundGeolocation.removeWatcher({ id: nativeWatcherId }).catch(() => {});
    nativeWatcherId = null;
  }
  if (webWatchId != null && navigator.geolocation) {
    navigator.geolocation.clearWatch(webWatchId);
    webWatchId = null;
  }
  if (sendTimer) { clearInterval(sendTimer); sendTimer = null; }
  latestFix = null; pendingFix = null; lastSentFix = null;
  session.active = false;
  session.server = null;
  session.startedAt = null;
  session.stopReason = reason;
  persist();
  setStatus('Idle');
}

// Foreground ⇄ background: iOS keeps delivering fixes to the native watcher while we are
// minimised; on resume we just re-sync the UI and flush anything held back. We never
// auto-start a session here.
if (isNativeApp()) {
  try {
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive && session.active) { sendNow(); notify(); }
    });
  } catch { /* App plugin unavailable */ }
}
